import React from "react";
import { Box, BoxProps, IconButton, Tooltip, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Pipette } from "lucide-react";
import clsx from "clsx";

import { ColorPicker } from "../fields/ColorPicker";

// EyeDropper API types (not yet in all TypeScript lib versions)
interface EyeDropperResult {
  sRGBHex: string;
}

interface EyeDropperAPI {
  open(): Promise<EyeDropperResult>;
}

declare global {
  interface Window {
    EyeDropper?: new () => EyeDropperAPI;
  }
}

const PRESET_COLORS = {
  white: "#FFFFFF",
} as const;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
    flexWrap: "nowrap",
  },

  eyedropperButton: {
    backgroundColor: theme.palette.common.white,
    border: "1px solid",
    borderColor: theme.palette.quote,
    borderRadius: theme.spacing(1),
    width: theme.spacing(10),
    height: theme.spacing(10),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(0.5),

    "&:hover": {
      backgroundColor: theme.palette.selected.main,
    },
  },

  eyedropperLabel: {
    fontWeight: 500,
    fontSize: 14,
    color: theme.palette.text.secondary,
    textTransform: "capitalize",
    marginBottom: theme.spacing(-2),
  },
}));

export interface SplashBackgroundColorPickerProps
  extends Omit<BoxProps, "onChange"> {
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
}

export function SplashBackgroundColorPicker(
  props: SplashBackgroundColorPickerProps,
) {
  const { className, value, onChange, disabled, ...other } = props;
  const s = useStyles();

  const eyeDropperSupported =
    typeof window !== "undefined" && "EyeDropper" in window;

  const normalizedValue = value?.toUpperCase() || PRESET_COLORS.white;
  const isPresetColor = Object.values(PRESET_COLORS).includes(
    normalizedValue as (typeof PRESET_COLORS)[keyof typeof PRESET_COLORS],
  );

  const handleSelect = React.useCallback(
    (color: string) => {
      if (onChange) {
        onChange(color.toUpperCase());
      }
    },
    [onChange],
  );

  const handleEyeDropper = React.useCallback(async () => {
    if (!eyeDropperSupported || !window.EyeDropper) return;

    try {
      const eyeDropper = new window.EyeDropper();
      const result = await eyeDropper.open();
      if (onChange) {
        onChange(result.sRGBHex.toUpperCase());
      }
    } catch (error) {
      // User canceled or API error - ignore
      console.debug("EyeDropper canceled or failed:", error);
    }
  }, [eyeDropperSupported, onChange]);

  return (
    <Box className={clsx(s.root, className)} {...other}>
      <ColorPicker
        label="white"
        value={PRESET_COLORS.white}
        checked={normalizedValue === PRESET_COLORS.white}
        onSelect={handleSelect}
        disabled={disabled}
      />
      <ColorPicker
        label="custom"
        value={isPresetColor ? "#000" : normalizedValue}
        checked={!isPresetColor}
        onSelect={handleSelect}
        disabled={disabled}
        dynamic
      />
      {eyeDropperSupported && (
        <Tooltip title="Pick color from screen">
          <IconButton
            className={s.eyedropperButton}
            onClick={handleEyeDropper}
            disabled={disabled}
          >
            <Pipette size={20} />
            <Typography className={s.eyedropperLabel}>pipette</Typography>
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}
